import { computed, watch } from "vue";
import { useServices, types } from "@/lib/openapi";
import { useRouter } from "vue-router";

import {
    AuthStatus,
    useAuthenticationManager,
    useAuthenticationStore,
} from "./authentication-store";
import { useOrganizationManager } from "@/composables/organization-manager";
import { defineStore, storeToRefs } from "pinia";
import { ref } from "vue";
import mixpanel from "mixpanel-browser";

export type MeUser = types.MeUser;
export type ClusterRole = types.ClusterRole;
// Ignore @typescript-eslint/no-redeclare.
// eslint-disable-next-line
export const ClusterRole = types.ClusterRole;

export const useUserStore = defineStore("user", () => {
    const { organization } = useOrganizationManager();
    const { signOut } = useAuthenticationStore();
    const { authStatus, authUser } = useAuthenticationManager();
    const router = useRouter();
    const user = ref<MeUser>();
    const currentOrganizationRole = ref<ClusterRole>();
    const controller = ref(new AbortController());

    const api = useServices({
        opts: {
            throwOnError: false,
        },
        requires: {
            auth: true,
            organization: false,
            stek: false,
        },
    });

    async function updateUser() {
        controller.value.abort();
        controller.value = new AbortController();

        try {
            const { response, data } = await api.getSelfUserMeGet({
                signal: controller.value.signal,
            });
            if (!response.ok) {
                if (response.status === 401) {
                    router.push({ name: "home" });
                    await signOut({
                        severity: "error",
                        summary: "Incorrect login.",
                    });
                    reset();
                } else {
                    throw new Error(await response.text());
                }
            } else {
                user.value = data;
                for (const [orgId, role] of Object.entries(user.value!.roles)) {
                    if (orgId === organization.value?.identity) {
                        currentOrganizationRole.value = role;
                        break;
                    }
                }
                mixpanel.identify(user.value?.identity);
                mixpanel.people.set_once({
                    "role": currentOrganizationRole.value,
                });
            }
        } catch (e) {
            if (e instanceof DOMException && e.name === "AbortError") {
                // Request was aborted by the AbortController.
                return;
            } else {
                throw e;
            }
        }
    }

    function reset() {
        user.value = undefined;
    }

    // When the organization changes, the permissions for the current user
    // change as well.
    watch(
        () => organization.value?.identity,
        () => {
            updateUser();
        }
    );

    watch(
        authStatus,
        (newAuthStatus) => {
            if (newAuthStatus !== AuthStatus.SIGNED_IN) {
                reset();
            } else {
                updateUser();
            }
        },
        { immediate: true }
    );

    return {
        user,
        currentOrganizationRole,
        reset,
        photoUrl: computed(() => authUser.value.photoUrl),
    };
});

export const useUserManager = () => storeToRefs(useUserStore());
